import { UseMutateAsyncFunction, useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router"
import { TriggerToast } from "../../components/globals/Toaster"
import { useAxios } from '../../contexts/AxiosContext'
import { Experiment } from "../../types"
import { appRoutes } from "../../routes/appRoutes"
import { registerCreatedExperiment } from "../../utils/queryInvalidation"

type DuplicateExperimentProps = {
  experimentId: string
}

type ErrorData = {
  error: string
  message: string
  statusCode: number
}

type ErrorResponse = {
  data: ErrorData
  status: number
}

type DuplicateExperimentError = {
  message: Array<string> | string
  statusCode: number
  error: string
  code: string
  response: ErrorResponse
}

type DuplicateExperimentResponse = {
  status: number
  message: string
  experiment: Experiment
}

type UseDuplicateExperimentType = UseMutateAsyncFunction<Experiment, unknown, DuplicateExperimentProps, unknown>

const useDuplicateExperiment = (): UseDuplicateExperimentType => {

  const { axiosApiInstance } = useAxios()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { mutateAsync: duplicateExperimentMutation } = useMutation({
    mutationFn: async (params: DuplicateExperimentProps) => {
      const response = await axiosApiInstance.post<DuplicateExperimentResponse>(`/experiment/experiment/${params.experimentId}/duplicate`, {})
      return response.data.experiment
    },
    onSuccess: async (experiment: Experiment) => {
      await registerCreatedExperiment(queryClient, experiment)
      navigate(appRoutes.experiments.setup(experiment.id))
    },
    onError: async (error: DuplicateExperimentError) => {
      if (error.response.data.message) {
        TriggerToast(error.response.data.message, undefined, 'error');
      } else {
        TriggerToast(error.message, undefined, 'error');
      }
    },
  })

  return duplicateExperimentMutation
}

export default useDuplicateExperiment
